export const MAX_CHECKPOINT_SLOTS = 4;

export const getEntityId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.id || "";
};

export const getEntityLabel = (value, fallbacks = []) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  for (const key of ["name", "title", "key", ...fallbacks]) {
    if (value[key]) return value[key];
  }
  return value._id || value.id || "";
};

export const normalizeCheckpoints = (data) => {
  if (Array.isArray(data)) return data;
  return data?.data || data?.checkpoints || data?.items || [];
};

export const getCheckpointApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const message = data?.message || error?.message || fallback;

  if (Array.isArray(data?.errors) && data.errors.length) {
    return `${message}: ${data.errors.join(", ")}`;
  }

  if (typeof data?.error === "string" && data.error) {
    return data.error;
  }

  return message;
};

export const getStageLabel = (stage, index = 0) => {
  if (!stage) return `Stage ${index + 1}`;
  if (stage.stage_name) return stage.stage_name;
  if (stage.stage_number === 0 || stage.stage_number) {
    return `Stage ${stage.stage_number}`;
  }
  return `Stage ${index + 1}`;
};

export const getTypeTokens = (type) => {
  if (!type) return [];
  if (typeof type === "string") return [type];
  return [type._id, type.id, type.key].filter(Boolean).map(String);
};

export const getCategoryTypeTokens = (category) => {
  const type = category?.typeId || category?.type;
  if (!type) return [];
  if (typeof type === "string") return [type];
  return [type._id, type.id, type.key].filter(Boolean).map(String);
};

export const categoryMatchesType = (category, selectedTypeId) => {
  if (!selectedTypeId) return false;
  const selected = String(selectedTypeId);
  return getCategoryTypeTokens(category).some(
    (token) => String(token) === selected
  );
};

export const countCheckpointsForStageCategory = (
  checkpoints = [],
  stageId,
  categoryId
) => {
  if (!stageId || !categoryId) return 0;
  return checkpoints.filter((checkpoint) => {
    const cpStageId = getEntityId(checkpoint?.stage_id || checkpoint?.stageId);
    const cpCategoryId = getEntityId(
      checkpoint?.category_id || checkpoint?.categoryId
    );
    return cpStageId === stageId && cpCategoryId === categoryId;
  }).length;
};

export const filterCheckpointsForStageCategory = (
  checkpoints = [],
  stageId,
  categoryId
) => {
  if (!stageId || !categoryId) return [];
  return checkpoints
    .filter((checkpoint) => {
      const cpStageId = getEntityId(
        checkpoint?.stage_id || checkpoint?.stageId
      );
      const cpCategoryId = getEntityId(
        checkpoint?.category_id || checkpoint?.categoryId
      );
      return cpStageId === stageId && cpCategoryId === categoryId;
    })
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
};

const pointsToText = (points) => {
  if (!Array.isArray(points)) return "";
  return points.filter(Boolean).join("\n");
};

const textToPoints = (text) =>
  String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const resolveMedia = (checkpoint) => {
  const media = checkpoint?.media;
  if (media && typeof media === "object" && media.url) {
    const type = media.type === "video" ? "video" : "image";
    return { mediaType: type, mediaUrl: String(media.url) };
  }

  if (checkpoint?.image) {
    return { mediaType: "image", mediaUrl: String(checkpoint.image) };
  }

  return { mediaType: "image", mediaUrl: "" };
};

export const emptyCheckpointSlot = (order = 1) => ({
  order,
  title: "",
  km_start: "",
  km_end: "",
  description_points_text: "",
  mediaType: "image",
  mediaUrl: "",
  imageFile: null,
  imagePreview: "",
  localError: "",
});

export const createEmptySlots = () =>
  Array.from({ length: MAX_CHECKPOINT_SLOTS }, (_, index) =>
    emptyCheckpointSlot(index + 1)
  );

export const mapFilteredCheckpointsToSlots = (checkpoints = []) => {
  const slots = createEmptySlots();
  const byOrder = new Map();

  (checkpoints || []).forEach((checkpoint) => {
    const order = Number(checkpoint?.order);
    if (!Number.isInteger(order) || order < 1 || order > MAX_CHECKPOINT_SLOTS) {
      return;
    }
    byOrder.set(order, checkpoint);
  });

  return slots.map((slot) => {
    const checkpoint = byOrder.get(slot.order);
    if (!checkpoint) return slot;

    const media = resolveMedia(checkpoint);
    return {
      ...slot,
      _id: checkpoint._id || checkpoint.id || "",
      title: checkpoint.title || "",
      km_start:
        checkpoint.km_start === 0 || checkpoint.km_start
          ? String(checkpoint.km_start)
          : "",
      km_end:
        checkpoint.km_end === 0 || checkpoint.km_end
          ? String(checkpoint.km_end)
          : "",
      description_points_text: pointsToText(checkpoint.description_points),
      mediaType: media.mediaType,
      mediaUrl: media.mediaUrl,
      imageFile: null,
      imagePreview: "",
    };
  });
};

export const isBlankSlot = (slot) => {
  if (!slot) return true;
  return !(
    String(slot.title || "").trim() ||
    String(slot.km_start || "").trim() ||
    String(slot.km_end || "").trim() ||
    String(slot.description_points_text || "").trim() ||
    String(slot.mediaUrl || "").trim() ||
    slot.imageFile
  );
};

export const isWebpFile = (file) => {
  if (!file) return false;
  if (file.type === "image/webp") return true;
  return /\.webp$/i.test(file.name || "");
};

const parseOptionalNumber = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const num = Number(raw);
  if (Number.isNaN(num) || !Number.isFinite(num)) return undefined;
  return num;
};

export const validateCheckpointSlots = (slots = [], { hadExisting = false } = {}) => {
  const errors = {};
  const filled = (slots || []).filter((slot) => !isBlankSlot(slot));

  if (!filled.length && !hadExisting) {
    errors.form = "Add at least one checkpoint, or cancel.";
    return { errors, filled };
  }

  const slotErrors = {};
  const seenOrders = new Set();

  filled.forEach((slot) => {
    const itemErrors = {};
    const order = Number(slot.order);

    if (!Number.isInteger(order) || order < 1 || order > MAX_CHECKPOINT_SLOTS) {
      itemErrors.order = "Order must be between 1 and 4.";
    } else if (seenOrders.has(order)) {
      itemErrors.order = "Orders must be unique.";
    } else {
      seenOrders.add(order);
    }

    if (!String(slot.title || "").trim()) {
      itemErrors.title = "Title is required.";
    }

    const kmStart = parseOptionalNumber(slot.km_start);
    if (slot.km_start !== "" && kmStart === undefined) {
      itemErrors.km_start = "Enter a valid km start.";
    }

    const kmEnd = parseOptionalNumber(slot.km_end);
    if (slot.km_end !== "" && kmEnd === undefined) {
      itemErrors.km_end = "Enter a valid km end.";
    }

    if (slot.mediaType === "image") {
      if (slot.imageFile) {
        if (!isWebpFile(slot.imageFile)) {
          itemErrors.media = "Only .webp images are allowed.";
        }
      } else if (!String(slot.mediaUrl || "").trim()) {
        itemErrors.media = "Upload a .webp image for this checkpoint.";
      }
    } else if (slot.mediaType === "video") {
      if (!String(slot.mediaUrl || "").trim()) {
        itemErrors.media = "Video URL is required when video is selected.";
      }
    } else {
      itemErrors.media = "Select image or video.";
    }

    if (Object.keys(itemErrors).length) {
      slotErrors[slot.order] = itemErrors;
    }
  });

  if (Object.keys(slotErrors).length) {
    errors.slots = slotErrors;
  }

  return { errors, filled };
};

export const resolveUploadUrl = (result) => {
  if (!result) return "";
  if (typeof result === "string") return result;
  return (
    result.url ||
    result.image_url ||
    result.imageUrl ||
    result.path ||
    result.file_url ||
    ""
  );
};

export const buildCheckpointCreatePayload = async ({
  slot,
  stageId,
  categoryId,
  eventId,
  typeId,
  uploadFile,
}) => {
  const payload = {
    stage_id: stageId,
    category_id: categoryId,
    event_id: eventId,
    type_id: typeId,
    order: Number(slot.order),
    title: String(slot.title || "").trim(),
  };

  const kmStart = parseOptionalNumber(slot.km_start);
  if (kmStart !== null && kmStart !== undefined) {
    payload.km_start = kmStart;
  }

  const kmEnd = parseOptionalNumber(slot.km_end);
  if (kmEnd !== null && kmEnd !== undefined) {
    payload.km_end = kmEnd;
  }

  const points = textToPoints(slot.description_points_text);
  if (points.length) {
    payload.description_points = points;
  }

  if (slot.mediaType === "image") {
    let url = String(slot.mediaUrl || "").trim();
    if (slot.imageFile) {
      const uploaded = await uploadFile(slot.imageFile);
      url = resolveUploadUrl(uploaded);
      if (!url) {
        throw new Error("Image upload succeeded but no URL was returned.");
      }
    }
    if (url) {
      payload.media = { type: "image", url };
    }
  } else if (slot.mediaType === "video") {
    const url = String(slot.mediaUrl || "").trim();
    if (url) {
      payload.media = { type: "video", url };
    }
  }

  return payload;
};

/**
 * Required save path for media: delete existing, then POST each filled slot.
 */
export const saveStageCategoryCheckpoints = async ({
  routeId,
  stageId,
  categoryId,
  eventId,
  typeId,
  slots,
  existingCheckpoints = [],
  deleteCheckpoint,
  createCheckpoint,
  uploadFile,
}) => {
  const hadExisting = existingCheckpoints.length > 0;
  const { errors, filled } = validateCheckpointSlots(slots, { hadExisting });
  if (Object.keys(errors).length) {
    const validationError = new Error("Validation failed");
    validationError.validationErrors = errors;
    throw validationError;
  }

  for (const checkpoint of existingCheckpoints) {
    const checkpointId = getEntityId(checkpoint);
    if (!checkpointId) continue;
    await deleteCheckpoint({ checkpointId });
  }

  const created = [];
  for (const slot of filled) {
    const payload = await buildCheckpointCreatePayload({
      slot,
      stageId,
      categoryId,
      eventId,
      typeId,
      uploadFile,
    });
    const result = await createCheckpoint({ routeId, payload });
    created.push(result);
  }

  return created;
};
